import sumBy from 'lodash/sumBy'
import { roundNumber } from '@ztechapps/common.utils'
import type { LogisticItem, Shipping } from '@ztechapps/common.contract'

export type ComputeLogisticAmountArgs = {
  items: LogisticItem[]
  isShippingCostIncluded: boolean
  shipping?: Shipping
}

export type ComputeLogisticAmountResult = {
  items: LogisticItem[]
  gross: number
  net: number
}

/**
 * Called from LogisticRepository.preSave, never a resolver. Rounds every line's `net` to two
 * decimal places FIRST, then sums the rounded values into `gross` — rounding before summing is
 * what keeps the printed lines adding up to the printed gross. `net` adds the carrier charge
 * on top of `gross` only when `isShippingCostIncluded` is set; the charge is never pushed into
 * `items`, since it is a record-level addend, not a line.
 */
export function computeLogisticAmount({
  items,
  isShippingCostIncluded,
  shipping,
}: ComputeLogisticAmountArgs): ComputeLogisticAmountResult {
  const roundedItems = (items ?? []).map((item) => ({ ...item, net: roundNumber(item.net ?? 0, 2) }))

  const gross = roundNumber(sumBy(roundedItems, 'net'), 2)
  const net = roundNumber(gross + (isShippingCostIncluded ? (shipping?.shippingCost ?? 0) : 0), 2)

  return { items: roundedItems, gross, net }
}
